import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AccountContext } from '../../services/AccountProvider';
import PropTypes from 'prop-types';
import { CAvatar, CDropdown, CDropdownItem, CDropdownMenu, CDropdownToggle } from '@coreui/react';
import ReactDOM from 'react-dom';
import arrowIcon from '../../assets/images/svg/ArrowDown.svg';
import SVGRenderer from '../../utils/svg/SVGRenderer';
import Buttons from '../buttons/Buttons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserInformation } from '../../services/TokenHandler';

const buildProcessedOptions = (list) => {
  const processedItems = [];
  let currentGroup = null;
  let groupItems = [];

  list.forEach((item, index) => {
    if (item.divider || item.isDivider) {
      if (currentGroup && groupItems.length > 0) {
        processedItems.push({ type: 'group', label: currentGroup, items: groupItems });
        groupItems = [];
      }
      processedItems.push({ type: 'separator', key: `divider-${index}` });
      currentGroup = null;
    } else if (item.label || item.section) {
      if (currentGroup && groupItems.length > 0) {
        processedItems.push({ type: 'group', label: currentGroup, items: groupItems });
        groupItems = [];
      }
      currentGroup = item.label || item.section;
    } else {
      if (currentGroup) {
        groupItems.push({ item, index });
      } else {
        processedItems.push({ type: 'item', item, index });
      }
    }
  });

  if (currentGroup && groupItems.length > 0) {
    processedItems.push({ type: 'group', label: currentGroup, items: groupItems });
  }

  return processedItems;
};

const OptionsMenu = ({ list }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = () => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const viewportPadding = 24;

    let top = triggerRect.bottom + 8;
    let left = triggerRect.right - menuRect.width;

    if (left < viewportPadding) left = viewportPadding;
    if (left + menuRect.width > window.innerWidth - viewportPadding) {
      left = window.innerWidth - menuRect.width - viewportPadding;
    }

    if (top + menuRect.height > window.innerHeight - viewportPadding) {
      top = triggerRect.top - menuRect.height - 8;
      if (top < viewportPadding) top = viewportPadding;
    }

    setPosition({ top, left });
  };

  const closeMenu = () => setOpen(false);

  const toggleMenu = () => {
    setOpen((prev) => !prev);
  };

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    const handleScrollOrResize = () => {
      closeMenu();
    };

    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open]);

  const processedItems = buildProcessedOptions(list);

  const renderItems = () => {
    return processedItems.map((processed, idx) => {
      if (processed.type === 'separator') {
        return <hr key={processed.key} className="shadcn-menu-separator" />;
      }

      const buildClassName = (item) => {
        const name = (item.linkName || item.name || '').toLowerCase();
        let className = 'shadcn-menu-item';
        if (name === 'deactivate' || name === 'delete') className += ' text-red-cus';
        if (name === 'activate') className += ' text-blue-cus';
        if (item.disabled) className += ' disabled';
        return className;
      };

      const buildContent = (item) => (
        <div className="shadcn-menu-item-content">
          <span className="shadcn-menu-item-text">{item.linkName || item.name}</span>
          {item.shortcut && <span className="shadcn-menu-shortcut">{item.shortcut}</span>}
          {item.hasSubmenu && <span className="shadcn-menu-chevron">›</span>}
        </div>
      );

      if (processed.type === 'group') {
        return (
          <React.Fragment key={`group-${idx}`}>
            <div className="shadcn-menu-label">{processed.label}</div>
            {processed.items.map(({ item, index: itemIndex }) => {
              const itemKey = item.linkName || item.name || `item-${itemIndex}`;
              const className = buildClassName(item);
              const content = buildContent(item);

              if (item.onclick) {
                return (
                  <button
                    key={itemKey}
                    type="button"
                    className={className}
                    onClick={() => {
                      if (!item.disabled) {
                        item.onclick();
                        closeMenu();
                      }
                    }}
                    disabled={item.disabled}
                  >
                    {content}
                  </button>
                );
              }

              return (
                <a key={itemKey} href={item.linkPath} className={className} onClick={closeMenu}>
                  {content}
                </a>
              );
            })}
          </React.Fragment>
        );
      }

      const item = processed.item;
      const itemKey = item.linkName || item.name || `item-${processed.index}`;
      const className = buildClassName(item);
      const content = buildContent(item);

      if (item.onclick) {
        return (
          <button
            key={itemKey}
            type="button"
            className={className}
            onClick={() => {
              if (!item.disabled) {
                item.onclick();
                closeMenu();
              }
            }}
            disabled={item.disabled}
          >
            {content}
          </button>
        );
      }

      return (
        <a key={itemKey} href={item.linkPath} className={className} onClick={closeMenu}>
          {content}
        </a>
      );
    });
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="CieloTable-Dropdown__Button"
        onClick={toggleMenu}
      >
        <SVGRenderer iconName="Options" />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="shadcn-menu"
            style={{ position: 'fixed', top: position.top, left: position.left }}
          >
            {renderItems()}
          </div>,
          document.body
        )}
    </>
  );
};

OptionsMenu.propTypes = {
  list: PropTypes.array.isRequired
};

const Dropdown = ({ dropdownType, list, image, userInitials }) => {
  const { t } = useTranslation();
  const { logout } = useContext(AccountContext);
  const userInfo = JSON.parse(getUserInformation());

  useEffect(() => {
    const span = document.createElement('span');
    span.id = 'closeMenu';
    document.body.appendChild(span);
    return () => span.remove();
  }, []);

  let navigate = useNavigate();

  const loginOut = () => {
    logout();
    navigate('/login');
  };

  const UserDropdown = () => {
    return (
      <div className="AvatarToggle">
        {image === 'xxxxx.png' ? (
          <CAvatar src={image} size="md" />
        ) : (
          <span className="UserInitials">{userInitials}</span>
        )}
        <div className="AvatarToggle-Information d-none d-lg-flex">
          <span className="AvatarToggle-Information__User">{`${userInfo.firstName} ${userInfo.lastName}`}</span>
          <span className="AvatarToggle-Information__Role">{userInfo.role.name}</span>
        </div>
        <div className="AvatarToggle-Arrow">
          <img src={arrowIcon} alt="arrow icon" />
        </div>
      </div>
    );
  };

  const OptionsDropdown = () => {
    return (
      <div className="CieloTable-Dropdown__Button">
        <SVGRenderer iconName="Options" />
      </div>
    );
  };

  const NotificationDropdown = () => {
    return (
      <div className="NotificationIcon position-relative">
        <img src={image} alt="Notification Icon" />
        <span className="position-absolute rounded-circle NotificationIcon-Dot">
          <span className="visually-hidden">{t('menu.new-alerts')}</span>
        </span>
      </div>
    );
  };

  const renderDropdownToggle = () => {
    switch (dropdownType) {
      case 'user':
        return UserDropdown();
      case 'notification':
        return NotificationDropdown();
      case 'options':
        return OptionsDropdown();
      default:
        break;
    }
  };

  const renderDropdownItems = () => {
    return list.map((item) => {
      if (item.onclick) {
        return (
          <Buttons
            onclick={() => {
              item.onclick();
              document.getElementById('closeMenu').click();
            }}
            style={{ color: item.color ?? '' }}
            key={item.linkName}
            className={
              item.linkName.toLowerCase() === 'deactivate'
                ? 'text-red-cus'
                : item.linkName.toLowerCase() === 'activate'
                ? 'text-blue-cus'
                : ''
            }
            disabled={item.disabled}
          >
            {item.linkName}
          </Buttons>
        );
      }

      if (item.linkName === 'Logout') {
        return (
          <CDropdownItem href={item.linkPath} key={item.linkName} onClick={loginOut}>
            <>{item.linkName}</>
          </CDropdownItem>
        );
      } else {
        return (
          <CDropdownItem
            href={item.linkPath}
            key={item.linkName}
            className={
              item.linkName.toLowerCase() === 'activate'
                ? 'text-blue-cus'
                : item.linkName.toLowerCase() === 'deactivate'
                ? 'text-red-cus'
                : ''
            }
          >
            {item.linkName}
          </CDropdownItem>
        );
      }
    });
  };

  if (dropdownType === 'options') {
    return <OptionsMenu list={list} />;
  }

  return (
    <CDropdown variant="nav-item">
      <CDropdownToggle placement="bottom-end" className="py-0" caret={false}>
        {renderDropdownToggle()}
      </CDropdownToggle>
      <CDropdownMenu className="py-3" placement="bottom-end">
        {renderDropdownItems()}
      </CDropdownMenu>
    </CDropdown>
  );
};

Dropdown.defaultProps = {
  userDropdown: '',
  image: ''
};

Dropdown.propTypes = {
  dropdownType: PropTypes.string,
  list: PropTypes.array.isRequired,
  image: PropTypes.string,
  userInitials: PropTypes.string
};

export default Dropdown;
